"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Check, MoveRight } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { HONEYPOT_FIELD, ELAPSED_FIELD } from "@/lib/form-fields";
import { formatLongDate } from "@/lib/availability";
import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "success" | "error";

/**
 * Team-training enquiry form. Submits to `/api/team-enquiry` via fetch with
 * inline success/error states (no full-page navigation). All fields use the
 * accessible `Input` component or a real `<label htmlFor>`.
 */
export function EnquiryForm() {
  const id = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const startedAt = useRef(Date.now());

  // Prefill the message when arriving from the booking calendar
  // (?date=YYYY-MM-DD&format=online|inperson&location=…).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const date = params.get("date");
    const format = params.get("format");
    const location = params.get("location");
    const match = date && /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match || (format !== "online" && format !== "inperson")) return;
    const picked = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    const what =
      format === "inperson" ? "an in-person full day" : "an online session";
    const where = location ? ` in ${location}` : "";
    setMessageDraft(
      `I would like to book ${what} on ${formatLongDate(picked)}${where}. Please confirm availability.`,
    );
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const payload = {
      name: String(data.get("name") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      company: String(data.get("company") ?? "").trim(),
      teamSize: String(data.get("teamSize") ?? "").trim(),
      message: String(data.get("message") ?? "").trim(),
      [HONEYPOT_FIELD]: String(data.get(HONEYPOT_FIELD) ?? ""),
      [ELAPSED_FIELD]: Date.now() - startedAt.current,
    };

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch("/api/team-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(
          body.error ?? "Something went wrong. Please try again.",
        );
      }
      setStatus("success");
      setMessage(
        body.message ??
          "Thank you. We’ve got your enquiry and will reply within one working day.",
      );
      form.reset();
      setMessageDraft("");
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        className="max-w-2xl rounded-md border border-success/40 bg-success/10 p-6"
      >
        <p className="flex items-center gap-2 font-heading text-lg font-semibold text-dark">
          <Check className="size-5 shrink-0 text-success" aria-hidden="true" />
          Enquiry sent
        </p>
        <p className="mt-2 text-sm text-mid">{message}</p>
      </div>
    );
  }

  const messageId = `${id}-message`;
  const errorId = `${id}-error`;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="max-w-2xl space-y-6 rounded-md border border-line bg-paper p-6 sm:p-8"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <Input
          label="Company name"
          name="company"
          placeholder="Acme Ltd"
          required
          autoComplete="organization"
        />
        <Input
          label="Your name"
          name="name"
          placeholder="Jane Smith"
          required
          autoComplete="name"
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Input
          label="Work email"
          name="email"
          type="email"
          placeholder="jane@acme.co.uk"
          required
          autoComplete="email"
        />
        <Input
          label="Team size"
          name="teamSize"
          type="number"
          inputMode="numeric"
          placeholder="e.g. 10"
          min={2}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={messageId}
          className="text-sm font-semibold text-dark"
        >
          Message{" "}
          <span className="font-normal text-mid">(optional)</span>
        </label>
        <textarea
          id={messageId}
          name="message"
          rows={4}
          value={messageDraft}
          onChange={(event) => setMessageDraft(event.target.value)}
          placeholder="Tell us about your training goals, timelines, or any questions…"
          className={cn(
            "min-h-[120px] w-full rounded-md border border-line bg-paper px-4 py-2.5",
            "text-base text-dark placeholder:text-mid/60 transition-colors duration-150 hover:border-mid/50",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
          )}
        />
      </div>

      {status === "error" && (
        <p
          id={errorId}
          role="alert"
          className="rounded-md border border-error/40 bg-error/10 px-4 py-3 text-sm font-medium text-error"
        >
          {message}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full sm:w-auto"
        disabled={status === "loading"}
        aria-describedby={status === "error" ? errorId : undefined}
      >
        {status === "loading" ? "Sending…" : "Send enquiry"}
        {status !== "loading" && (
          <MoveRight className="size-5" aria-hidden="true" />
        )}
      </Button>

      <HoneypotField />
    </form>
  );
}
